import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Menu, X, Phone } from "lucide-react";

// Pages
import HomePage from "@/pages/HomePage";
import TheSpacePage from "@/pages/TheSpacePage";
import WeddingsPage from "@/pages/WeddingsPage";
import GalleryPage from "@/pages/GalleryPage";
import ReviewsPage from "@/pages/ReviewsPage";
import ContactPage from "@/pages/ContactPage";
import AdminPage from "@/pages/AdminPage";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_eco-retreat-2/artifacts/devt2tjr_WhatsApp%20Image%202026-02-13%20at%2014.30.10.jpeg";

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/the-space", label: "The Space" },
    { path: "/weddings", label: "Weddings" },
    { path: "/gallery", label: "Gallery" },
    { path: "/reviews", label: "Reviews" },
    { path: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-brand-border">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Weaver's Nest" className="h-14 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm ${isActive(link.path) ? 'text-brand-sage' : ''}`}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a href="tel:+27832329940" className="flex items-center gap-2 text-brand-charcoal text-sm">
              <Phone className="w-4 h-4" />
              <span>+27 83 232 9940</span>
            </a>
            <a 
              href="https://api.whatsapp.com/send?phone=%2B27832329940&text=Hi%2C%20I%20would%20like%20to%20book%20a%20stay%20at%20Weaver%27s%20Nest%20Cottage" 
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-sm py-3 px-6"
              data-testid="nav-book-now"
            >
              Book via WhatsApp
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - Solid Background */}
      {mobileMenuOpen && (
        <div 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: '#F8F5EF',
            zIndex: 99999,
            paddingTop: '100px',
            paddingLeft: '24px',
            paddingRight: '24px',
            overflowY: 'auto',
            display: 'block'
          }}
        >
          {/* Close button at top */}
          <button 
            onClick={() => setMobileMenuOpen(false)}
            style={{ position: 'absolute', top: '28px', right: '16px' }}
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`text-lg font-medium py-2 ${isActive(link.path) ? 'text-brand-sage' : 'text-brand-charcoal'}`}
              >
                {link.label}
              </Link>
            ))}
            <hr className="border-brand-border my-4" />
            <a 
              href="https://api.whatsapp.com/send?phone=%2B27832329940&text=Hi%2C%20I%20would%20like%20to%20book%20a%20stay%20at%20Weaver%27s%20Nest%20Cottage"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMobileMenuOpen(false)}
              className="btn-primary text-center"
            >
              Book via WhatsApp
            </a>
            <a 
              href="tel:+27832329940" 
              className="flex items-center justify-center gap-2 text-brand-charcoal py-3"
            >
              <Phone className="w-5 h-5" />
              <span>+27 83 232 9940</span>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

const WhatsAppButton = () => (
  <a
    href="https://api.whatsapp.com/send?phone=%2B27832329940"
    target="_blank"
    rel="noopener noreferrer"
    className="whatsapp-float"
    data-testid="whatsapp-btn"
    aria-label="Contact us on WhatsApp"
  >
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  </a>
);

const Footer = () => (
  <footer className="footer">
    <div className="container mx-auto px-4 md:px-8 max-w-7xl">
      <div className="grid md:grid-cols-4 gap-8 mb-12">
        {/* Logo & About */}
        <div className="md:col-span-2">
          <img src={LOGO_URL} alt="Weaver's Nest" className="h-16 mb-4 rounded-lg" />
          <p className="text-gray-400 text-sm leading-relaxed max-w-md">
            Nestled between ancient Milkwoods and indigenous bush, Weaver's Nest is an oasis 
            of tranquility and luxury in Sardinia Bay, Gqeberha.
          </p>
        </div>
        
        {/* Quick Links */}
        <div>
          <h4 className="font-heading text-lg mb-4">Quick Links</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/the-space">The Space</Link></li>
            <li><Link to="/weddings">Weddings & Bridal</Link></li>
            <li><Link to="/gallery">Gallery</Link></li>
            <li><a href="https://api.whatsapp.com/send?phone=%2B27832329940&text=Hi%2C%20I%20would%20like%20to%20book%20a%20stay%20at%20Weaver%27s%20Nest%20Cottage" target="_blank" rel="noopener noreferrer">Book via WhatsApp</a></li>
            <li><Link to="/contact">Contact</Link></li>
          </ul>
        </div>
        
        {/* Contact */}
        <div>
          <h4 className="font-heading text-lg mb-4">Contact</h4>
          <ul className="space-y-2 text-sm text-gray-400">
            <li>Sardinia Bay, Gqeberha</li>
            <li>South Africa</li>
            <li className="pt-2">
              <a href="tel:+27832329940" className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                +27 83 232 9940
              </a>
            </li>
          </ul>
        </div>
      </div>
      
      <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-gray-500 text-sm">
          © {new Date().getFullYear()} Weaver's Nest Cottage. All rights reserved.
        </p>
        <div className="flex gap-6 text-sm text-gray-500">
          <Link to="/admin">Admin</Link>
        </div>
      </div>
    </div>
  </footer>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Navigation />
        <main className="pt-20">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/the-space" element={<TheSpacePage />} />
            <Route path="/weddings" element={<WeddingsPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/admin" element={<AdminPage />} />
          </Routes>
        </main>
        <Footer />
        <WhatsAppButton />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
